// One ranked group card — real fields only: member projects (resolved from
// the portfolio project list), the fourth-anchor-phrasing score, and — when
// present — the real lifecycle_status (Task #5311, live on dev; see
// types/whitespace.ts's note). Verdict is derived client-side from `score`
// via overlapRelationshipHeadline — design §5.1's own rule that the
// sentence is computed, never disagreeing with the number. The design's own
// entity/inventor/funding chips are NOT in the served payload yet — see the
// DATA GAPS note on OverlapGroup — so this card never renders them as
// fabricated placeholders, it simply omits them.
import { overlapGroupMetaLine, overlapRelationshipHeadline, overlapTierColor, overlapTierForScore } from '../../lib/whitespace'
import type { OverlapGroup, OverlapGroupLifecycleStatus } from '../../types/whitespace'

const LIFECYCLE_BADGE_LABELS: Record<OverlapGroupLifecycleStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  consolidating: 'Consolidating',
  consolidated: 'Consolidated',
  dismissed: 'Dismissed',
}

interface GroupCardProps {
  group: OverlapGroup
  projectNames: Map<string, string>
  selected: boolean
  onSelect: () => void
}

export default function GroupCard({ group, projectNames, selected, onSelect }: GroupCardProps) {
  const tier = overlapTierForScore(group.score)
  const colors = overlapTierColor(tier)
  const headline = overlapRelationshipHeadline(group.score, group.member_project_ids.length)

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={`overlap-group-card-${group.group_id}`}
      className={`text-left card p-3 w-full transition-colors ${
        selected ? 'border-2 border-iflow-500 shadow-md' : 'border border-gray-200 hover:bg-sky-50/40'
      }`}
    >
      <div className="flex items-center gap-1.5">
        <div className="text-[13.5px] font-extrabold text-iflow-900">{headline}</div>
        {group.lifecycle_status && (
          <span className="text-[9.5px] font-bold rounded-full px-1.5 py-0.5 bg-iflow-100 text-iflow-800" data-testid="overlap-group-lifecycle-badge">
            {LIFECYCLE_BADGE_LABELS[group.lifecycle_status]}
          </span>
        )}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{overlapGroupMetaLine(group)}</div>
      <div
        className="text-[12px] font-bold mt-1.5 inline-block px-1.5 py-0.5 rounded"
        style={{ background: colors.background, color: colors.color }}
      >
        {headline}
      </div>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {group.member_project_ids.map((projectId) => (
          <span key={projectId} className="chip text-[10.5px] px-2 py-0.5 rounded-full bg-sky-100 text-iflow-800 font-medium">
            {projectNames.get(projectId) ?? projectId}
          </span>
        ))}
      </div>
    </button>
  )
}
